import { Button, Modal, useToast } from '@citizen-walk/shared-ui';
import { useEffect, useState } from 'react';
import { useDeviceTransferIssue } from './useDeviceTransfer';

export interface DeviceTransferModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * デバイス間引継コード発行モーダル (Sprint BG-DataSettings 8、GAP U-C9)。
 *
 * 旧 `old/apps/user/src/components/DeviceTransferModal.tsx` (128 行、L3 参照) を踏襲:
 *   - 「8 文字 token を発行」ボタン → callable → token 表示 + 残り時間カウントダウン + コピーボタン
 *   - 「PWA で受け取る」 helper text + 手順表示
 *
 * 旧との差分 (v2):
 *   - **shared-ui Modal + Button** に置換 (旧の inline Tailwind を Modal に統合、a11y 統一)
 *   - **token 形式**: 旧は 8 文字 base32 + XXXX-XXXX 表示、v2 は 8 文字一括表示 (1/I/0/O 除外は踏襲)
 *   - **districtCount 表示廃止**: 旧は token doc に snapshot 埋込で件数表示可能だったが、v2 は
 *     consume 時に都度 read する設計 (発行〜消費間に新規参加した地区も移行できる利点) のため非表示
 *   - **state 管理**: v2 useDeviceTransferIssue hook で idle/pending/success/error の状態機械化
 *
 * Region: callable `createDeviceTransferToken` は asia-northeast1。
 */
export function DeviceTransferModal({ open, onClose }: DeviceTransferModalProps) {
  const { state, call, reset } = useDeviceTransferIssue();
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  // モーダル閉じた時に state + copied をリセット
  useEffect(() => {
    if (!open) {
      reset();
      setCopied(false);
    }
  }, [open, reset]);

  // success 時の Toast
  const status = state.status;
  const errorMsg = state.status === 'error' ? state.error.message : null;

  useEffect(() => {
    if (status === 'success') {
      toast('success', '引継コードを発行しました');
    } else if (status === 'error') {
      toast('error', `発行に失敗: ${errorMsg ?? '不明なエラー'}`);
    }
  }, [status, errorMsg, toast]);

  async function handleCopy(token: string) {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast('info', 'クリップボードにコピーしました');
    } catch {
      toast('error', 'コピーに失敗しました');
    }
  }

  // old2 入口フロー移植 Phase D: カウントダウンを撤去し、絶対時刻『有効期限: HH:MM:SS』表示へ。
  const expiresAtLabel =
    state.status === 'success' ? new Date(state.data.expiresAt).toLocaleTimeString() : '';

  return (
    <Modal open={open} onClose={onClose} title="📲 ホーム画面のアプリに移す" size="md">
      <p className="text-sm text-slate-700 mb-3">
        ホーム画面に追加したアプリは新しいアカウント扱いになります。ここで
        <strong>番号</strong>
        を発行し、追加したアプリを起動してトップ画面の「ブラウザから引き継ぎ」欄に入力してください。
        参加済みの地区が一括でコピーされます。
      </p>
      {/* R-70 UI 注意喚起: claim (linkedUids) は MRU 上限 10 のため、多端末では最古端末が編集不可になりうる。 */}
      <p className="text-xs text-slate-500 mb-4">
        たくさんの端末で引き継ぎを繰り返すと、いちばん古い端末では投稿やカードの編集・削除ができなくなる場合があります（閲覧は引き続き行えます）。編集は最近お使いの端末で行ってください。
      </p>

      {state.status === 'idle' && (
        <Button variant="primary" size="md" onClick={() => void call()} className="w-full">
          引継コードを生成
        </Button>
      )}

      {state.status === 'pending' && <p className="text-center text-slate-500 py-4">生成中…</p>}

      {state.status === 'success' && (
        <>
          <div className="bg-warning-50 border border-warning-300 rounded-lg p-4 mb-3">
            <p className="text-xs text-slate-600 mb-2">
              引き継ぎコード（{state.data.districtCount} 地区分・10 分有効・1 回限り）
            </p>
            <div className="flex items-center gap-2 mb-2">
              <code className="text-2xl font-mono font-bold tracking-widest select-all flex-1 text-center">
                {state.data.token}
              </code>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void handleCopy(state.data.token)}
              >
                {copied ? '✓ コピー済' : 'コピー'}
              </Button>
            </div>
            {/* old2 入口フロー移植 Phase D: カウントダウンを撤去し、絶対時刻表記へ。 */}
            <p className="text-xs text-slate-500" data-testid="device-transfer-expires-at">
              有効期限: {expiresAtLabel}
            </p>
          </div>

          <p className="text-xs text-slate-700 font-medium mb-1">次の手順:</p>
          <ol className="text-xs text-slate-700 list-decimal list-inside space-y-0.5 mb-3">
            <li>このコードを控える (スクショ／メモ)</li>
            <li>ブラウザの共有メニューから「ホーム画面に追加」</li>
            <li>追加したアプリを起動</li>
            <li>トップ画面「ブラウザから引き継ぎ」欄にコードを入力</li>
          </ol>
        </>
      )}

      {state.status === 'error' && (
        <>
          <p className="text-sm text-red-700 bg-red-50 p-3 rounded border border-red-200 mb-3">
            {state.error.message}
          </p>
          <Button variant="primary" size="md" onClick={() => void call()} className="w-full">
            再試行
          </Button>
        </>
      )}

      <div className="flex justify-end mt-4">
        <Button variant="secondary" size="md" onClick={onClose}>
          閉じる
        </Button>
      </div>
    </Modal>
  );
}
